import type { Download, OptionDB } from "@project/emerge";
import { AbstractProductDefinition } from "@project/emerge";
import type { AbstractPartDefinition } from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import type { PriceData } from "@project/pricing";
import groupBy from "lodash/groupBy";
import type { Group, Vector3 } from "three";
import { GroupAnimationManager } from "../animations/GroupAnimationManager";
import { AddableBuildStep } from "../buildsteps/AddableBuildStep";
import { AngleSectionBuildStep } from "../buildsteps/AngleSectionBuildStep";
import { CaseInteriorBuildStep } from "../buildsteps/CaseInteriorBuildStep";
import { CornerBraceBuildStep } from "../buildsteps/CornerBraceBuildStep";
import { CornerBuildStep } from "../buildsteps/CornerBuildStep";
import { CoverPaddingBuildStep } from "../buildsteps/CoverPaddingBuildStep";
import { FlightCaseBlueprintCalculator } from "../buildsteps/FlightCaseBlueprintCalculator";
import { OuterPanelBuildStep } from "../buildsteps/OuterPanelBuildStep";
import { ProfileBuildStep } from "../buildsteps/ProfileBuildStep";
import { Rack19InteriorBuildStep } from "../buildsteps/Rack19InteriorBuildStep";
import { RackInteriorBuildStep } from "../buildsteps/RackInteriorBuildStep";
import { RollerBuildStep } from "../buildsteps/RollerBuildStep";
import { RunnerBuildStep } from "../buildsteps/RunnerBuildStep";
import { PadDownloadGenerator } from "../downloads/PadDownload";
import { PanelDownloadGenerator } from "../downloads/PanelDownload";
import { PartsListDownload } from "../downloads/PartsListDownload";
import { ProfileDownloadGenerator } from "../downloads/ProfileDownload";
import { AddableType } from "../enum/AddableType";
import { ConnectorCategory } from "../enum/ConnectorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTProducts } from "../enum/LTProducts";
import { LTPartType } from "../enum/PartType";
import { VariableOptionType } from "../enum/VariableOptionType";
import { SupplyPartPanelInteraction } from "../interactions/SupplyPartPanelInteraction";
import type { AddableGridInfos } from "../interfaces/AddableGridInfos";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";
import type { CutProperties } from "../interfaces/CutProperties";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { OuterPanelProperties } from "../interfaces/OuterPanelProperties";
import type { PadDefinition } from "../interfaces/PadProperties";
import type { ProfileProperties } from "../interfaces/ProfileProperties";
import type { AngleSectionDefinition } from "../parts/AngleSectionDefinition";
import { hydrateImportedConnectorPartSelections } from "../settings/hydrateImportedConnectorPartSelections";
import { hydrateImportedCornerPartSelections } from "../settings/hydrateImportedCornerPartSelections";
import { hydrateImportedCreeperRollers } from "../settings/hydrateImportedCreeperRollers";
import { hydrateImportedExteriorRollers } from "../settings/hydrateImportedExteriorRollers";
import { createAddableGrid } from "../structure/AddableInfos";
import { createConnectorSet } from "../structure/ConnectorSetInfos";
import { createRollerBoard } from "../structure/RollerBoardInfos";
import { createRunners } from "../structure/RunnerInfos";
import type { VariableOptions } from "../types/VariableOptions";
import { CURRENT_PRODUCT_SETTINGS_VERSION } from "../user_options/constants";
import { createCaseInterior } from "../user_options/InteriorOptions";
import type { RollerBoardTypes } from "../user_options/RollerType";
import {
  type AddableMarginDragRanges,
  clampMarginToRange,
  computeAddableMarginRangesFromDragPart,
  intersectMarginRanges,
} from "../utils/addableMarginDragRange";
import {
  type ConnectorMarginAxisRange,
  computeConnectorMarginRangeForEdgePart,
  intersectConnectorMarginRanges,
} from "../utils/connectorMarginDragRange";

// Simple interface for animation state
interface GroupAnimationState {
  isOpen: boolean;
  isAnimating: boolean;
}

export class FlightCaseDefinition extends AbstractProductDefinition<
  FlightCaseSettings,
  FlightCaseBlueprint,
  // biome-ignore lint/suspicious/noExplicitAny: buildsteps emit heterogeneous part defs; not all satisfy AbstractPartDefinition (e.g. PianoHingeDefinition).
  any
> {
  private blueprintCalculator = new FlightCaseBlueprintCalculator();
  private groupAnimationManager = new GroupAnimationManager();
  /** Margin ranges from the last build, keyed by option uuid. */
  private addableMarginRanges = new Map<string, AddableMarginDragRanges>();
  private connectorMarginRanges = new Map<string, ConnectorMarginAxisRange>();

  constructor(settings: FlightCaseSettings, optionDb: OptionDB) {
    // Hydrators replace legacy/partial slices (corners, rollers, connectors)
    // in-place. Callers often pass a frozen draft (react-hook-form / immer /
    // DB response + deep-freeze in dev), so mutating the original settings
    // throws "… is read-only". Work on a structured clone so hydration is
    // always safe; the product owns its own settings copy anyway.
    const ownedSettings = structuredClone(settings) as FlightCaseSettings;
    hydrateImportedCreeperRollers(ownedSettings, optionDb);
    hydrateImportedExteriorRollers(ownedSettings, optionDb);
    hydrateImportedConnectorPartSelections(ownedSettings, optionDb);
    hydrateImportedCornerPartSelections(ownedSettings, optionDb);
    super(ownedSettings);

    // Initialize buildsteps
    this.buildSteps = [
      new OuterPanelBuildStep(),
      new ProfileBuildStep(),
      new AngleSectionBuildStep(),
      new CornerBraceBuildStep(),
      new CornerBuildStep(),
      new RollerBuildStep(),
      new RunnerBuildStep(),
      // Interior buildsteps - each handles one interior type and self-filters
      new CaseInteriorBuildStep(), // CASE: volumes, tray panels, pads
      new RackInteriorBuildStep(), // RACK: tray panel dividers
      new Rack19InteriorBuildStep(), // RACK19: rack rails, underlay panels
      // Cover padding - handles all interior types
      new CoverPaddingBuildStep(),
      new AddableBuildStep(),
    ];

    // Initialize part interactions
    this.partInteractions = [
      new SupplyPartPanelInteraction(), // Cutouts von SupplyParts auf Außenplatten und Polster (Pads)
    ];
  }

  protected generateBlueprint(
    settings: FlightCaseSettings,
  ): FlightCaseBlueprint {
    return this.blueprintCalculator.execute(settings);
  }
  protected updateBlueprint(
    settings: FlightCaseSettings,
    _blueprint: FlightCaseBlueprint,
    _changes: Partial<FlightCaseSettings>,
  ): FlightCaseBlueprint {
    return this.blueprintCalculator.execute(settings);
  }

  /**
   * Clamp addable margins ("Abstand") back into their valid drag area after a
   * rebuild. The drag area is defined by the case geometry, so resizing the
   * case, switching the alignment between Case and Korpus, or changing the
   * count/arrangement can leave a stored margin outside the area — the part then
   * sits outside its allowed bounds. We re-derive each addable's valid margin
   * range from the freshly built surface drag constraints (the same padded
   * bounds used for Ctrl+drag) and clamp the stored margins back in.
   *
   * Connector sets (locks/hinges) get the same treatment on their shared
   * `margin` along the profile edge.
   *
   * Idempotent: the range depends only on geometry (not on the margin), so once
   * the margins are clamped a second pass finds them in range and returns null.
   */
  public override normalizeAfterBuild(): Partial<FlightCaseSettings> | null {
    const addableRanges = this.collectAddableMarginRanges();
    const connectorRanges = this.collectConnectorMarginRanges();
    this.addableMarginRanges = addableRanges;
    this.connectorMarginRanges = connectorRanges;
    if (addableRanges.size === 0 && connectorRanges.size === 0) {
      return null;
    }
    return this.clampVariableOptionMargins(addableRanges, connectorRanges);
  }

  /**
   * `option` with the margins this case would settle it at, i.e. the clamp
   * `normalizeAfterBuild` applies to the option of the same uuid.
   *
   * Lets a caller ask "is this config still the one I applied?" without the
   * build's own correction reading as a user edit: a Vorlage saved on a case
   * whose geometry allowed a smaller margin cannot be reproduced verbatim here,
   * and comparing against the raw Vorlage would report every such config as
   * modified the moment the first rebuild lands.
   *
   * Uses the ranges from the last build, so the answer only means anything for
   * an option the current product was built with.
   */
  public realizeVariableOption(option: VariableOptions): VariableOptions {
    if (!option?.uuid) {
      return option;
    }
    return this.clampOptionMargins(
      option,
      this.addableMarginRanges.get(option.uuid),
      this.connectorMarginRanges.get(option.uuid),
    );
  }

  /**
   * Valid margin range per addable option uuid. Built parts give the most
   * accurate bounds (their actual target rect); options whose margin pushed the
   * part fully off its rect (no part emitted) fall back to a geometry probe.
   */
  private collectAddableMarginRanges(): Map<string, AddableMarginDragRanges> {
    const optionByUuid = new Map<string, AddableGridInfos>();
    for (const option of this.settings.variableOptions) {
      if (option?.uuid) {
        optionByUuid.set(option.uuid, option as AddableGridInfos);
      }
    }

    const rangesByUuid = new Map<string, AddableMarginDragRanges>();

    // A grid/mirror can emit several parts that share one option; intersect
    // their ranges so the clamped margin keeps every part inside its area.
    for (const part of this.partDefinitions.values()) {
      const constraint = part.dragConstraint;
      if (constraint?.type !== "surface" || !constraint.sourceOptionUuid) {
        continue;
      }
      const uuid = constraint.sourceOptionUuid;
      const option = optionByUuid.get(uuid);
      if (!option) {
        continue;
      }
      const range = computeAddableMarginRangesFromDragPart(
        part as AbstractPartDefinition<string>,
        option,
      );
      if (!range) {
        continue;
      }
      const existing = rangesByUuid.get(uuid);
      rangesByUuid.set(
        uuid,
        existing ? intersectMarginRanges(existing, range) : range,
      );
    }

    this.addGeometryMarginRangeFallback(rangesByUuid);
    return rangesByUuid;
  }

  /** Geometry-only range for addables that emitted no resolvable part. */
  private addGeometryMarginRangeFallback(
    rangesByUuid: Map<string, AddableMarginDragRanges>,
  ): void {
    const addableStep = this.buildSteps.find(
      (step): step is AddableBuildStep => step instanceof AddableBuildStep,
    );
    if (!addableStep || !this.blueprint) {
      return;
    }
    for (const option of this.settings.variableOptions) {
      if (
        !option?.uuid ||
        rangesByUuid.has(option.uuid) ||
        !("addableType" in option)
      ) {
        continue;
      }
      const range = addableStep.computeMarginRangesForOption(
        this.blueprint,
        this.settings,
        option,
      );
      if (range) {
        rangesByUuid.set(option.uuid, range);
      }
    }
  }

  private collectConnectorMarginRanges(): Map<
    string,
    ConnectorMarginAxisRange
  > {
    const optionByUuid = new Map<string, ConnectorSetInfos>();
    for (const option of this.settings.variableOptions) {
      if (
        option?.uuid &&
        (option.variableOptionType === VariableOptionType.LOCK_SET ||
          option.variableOptionType === VariableOptionType.HINGE_SET)
      ) {
        optionByUuid.set(option.uuid, option as ConnectorSetInfos);
      }
    }

    const rangesByUuid = new Map<string, ConnectorMarginAxisRange>();

    for (const part of this.partDefinitions.values()) {
      const constraint = part.dragConstraint;
      if (constraint?.type !== "edge" || !constraint.sourceOptionUuid) {
        continue;
      }
      const uuid = constraint.sourceOptionUuid;
      const option = optionByUuid.get(uuid);
      if (!option) {
        continue;
      }
      const range = computeConnectorMarginRangeForEdgePart(
        part as AbstractPartDefinition<string>,
        option,
      );
      if (!range) {
        continue;
      }
      const existing = rangesByUuid.get(uuid);
      const merged = existing
        ? intersectConnectorMarginRanges([existing, range])
        : range;
      if (merged) {
        rangesByUuid.set(uuid, merged);
      }
    }

    this.addConnectorMarginRangeFallback(rangesByUuid);
    return rangesByUuid;
  }

  /** Geometry-only range when margin pushed connectors off the edge (no drag part). */
  private addConnectorMarginRangeFallback(
    rangesByUuid: Map<string, ConnectorMarginAxisRange>,
  ): void {
    const profileStep = this.buildSteps.find(
      (step): step is ProfileBuildStep => step instanceof ProfileBuildStep,
    );
    if (!profileStep || !this.blueprint) {
      return;
    }
    for (const option of this.settings.variableOptions) {
      if (
        !option?.uuid ||
        rangesByUuid.has(option.uuid) ||
        (option.variableOptionType !== VariableOptionType.LOCK_SET &&
          option.variableOptionType !== VariableOptionType.HINGE_SET)
      ) {
        continue;
      }
      const range = profileStep.computeMarginRangesForConnectorOption(
        this.blueprint,
        this.settings,
        option as ConnectorSetInfos,
      );
      if (range) {
        rangesByUuid.set(option.uuid, range);
      }
    }
  }

  /**
   * The option with both margin kinds clamped into the given ranges. Returns the
   * option itself when nothing moved, so callers can detect "unchanged" by
   * identity.
   */
  private clampOptionMargins(
    option: VariableOptions,
    addableRange: AddableMarginDragRanges | undefined,
    connectorRange: ConnectorMarginAxisRange | undefined,
  ): VariableOptions {
    let next = option;

    if (addableRange && "addableType" in option) {
      const addable = option as AddableGridInfos;
      const horizontalMargin = clampMarginToRange(
        addable.horizontalMargin,
        addableRange.horizontal,
      );
      const verticalMargin = clampMarginToRange(
        addable.verticalMargin,
        addableRange.vertical,
      );
      if (
        horizontalMargin !== addable.horizontalMargin ||
        verticalMargin !== addable.verticalMargin
      ) {
        next = { ...addable, horizontalMargin, verticalMargin };
      }
    }

    if (
      connectorRange &&
      (option.variableOptionType === VariableOptionType.LOCK_SET ||
        option.variableOptionType === VariableOptionType.HINGE_SET)
    ) {
      const connector = next as ConnectorSetInfos;
      const margin = clampMarginToRange(connector.margin, connectorRange);
      if (margin !== connector.margin) {
        next = { ...connector, margin };
      }
    }

    return next;
  }

  private clampVariableOptionMargins(
    addableRangesByUuid: Map<string, AddableMarginDragRanges>,
    connectorRangesByUuid: Map<string, ConnectorMarginAxisRange>,
  ): Partial<FlightCaseSettings> | null {
    let changed = false;
    const variableOptions = this.settings.variableOptions.map((option) => {
      if (!option?.uuid) {
        return option;
      }

      const next = this.clampOptionMargins(
        option,
        addableRangesByUuid.get(option.uuid),
        connectorRangesByUuid.get(option.uuid),
      );
      if (next !== option) {
        changed = true;
      }
      return next;
    });

    return changed ? { variableOptions } : null;
  }

  /** Apply the ranges to the stored margins; returns a patch only if changed. */
  private clampAddableMargins(
    rangesByUuid: Map<string, AddableMarginDragRanges>,
  ): Partial<FlightCaseSettings> | null {
    return this.clampVariableOptionMargins(rangesByUuid, new Map());
  }

  /**
   * Returns the variable options available for this product.
   */
  public getAllowedVariableOptions(
    optionDb: OptionDB,
    cuts: CutProperties[],
  ): {
    [key in keyof typeof VariableOptionType]: () => VariableOptions;
  } {
    return FlightCaseDefinition.getAllowedVariableOptions(optionDb, cuts);
  }

  /**
   * A static helper to provide default settings
   */
  public static getSerializedDefaultVersion(
    optionDb: OptionDB,
    settingsOverride: Partial<FlightCaseSettings> & object = {},
  ): FlightCaseSettings {
    return {
      ...settingsOverride,
      coverPaddings: {},
      cuts: [],
      exteriorOptions: {
        angleSections: optionDb.getByName(
          LTCaseUserOption.AngleSectionType,
          "M1378",
        ),
        color: optionDb.getByName(LTCaseUserOption.PanelColor, "Phenol"),
        cornerBraces: optionDb.getByName(
          LTCaseUserOption.CornerBraceType,
          "AH4043A",
        ),
        cornerBracesCover: optionDb.getByName(
          LTCaseUserOption.CornerBraceType,
          "AH4043A",
        ),
        cornersBottom: optionDb.getByName(
          LTCaseUserOption.CornerType,
          "AH41201LT",
        ),
        cornersTop: optionDb.getByName(
          LTCaseUserOption.CornerType,
          "AH41201LT",
        ),
        profiles: optionDb.getByName(LTCaseUserOption.ProfileType, "M6102"),
        rollers: optionDb.getByName(
          LTCaseUserOption.RollerType,
          "Keine",
        ) as RollerBoardTypes,
        strength: 6.5,
      },
      interiorOptions: createCaseInterior(optionDb),
      options: {} as Record<string, unknown>,
      type: LTProducts.Haube,
      variableOptions: [],
      version: CURRENT_PRODUCT_SETTINGS_VERSION,
    };
  }

  public static getAllowedVariableOptions(
    optionDb: OptionDB,
    cuts: CutProperties[],
  ): {
    [key in keyof typeof VariableOptionType]: () => VariableOptions;
  } {
    return {
      [VariableOptionType.LOCK_SET]: () =>
        createConnectorSet(
          optionDb,
          ConnectorCategory.Lock,
          cuts[0]?.groupName || "",
          Sides.LEFT,
          "AH17291S",
        ),
      [VariableOptionType.HINGE_SET]: () =>
        createConnectorSet(
          optionDb,
          ConnectorCategory.Hinge,
          cuts[0]?.groupName || "",
          Sides.LEFT,
          "AH2524",
        ),
      [VariableOptionType.FEET]: () =>
        createAddableGrid(optionDb, AddableType.Foot, Sides.BOTTOM, "AH4904", {
          horizontalCount: 2,
          verticalCount: 2,
        }),
      [VariableOptionType.HANDLES]: () =>
        createAddableGrid(optionDb, AddableType.Handle, Sides.FRONT, "AH3415"),
      [VariableOptionType.DISHES]: () =>
        createAddableGrid(optionDb, AddableType.Dish, Sides.TOP, "AH88001D"),
      [VariableOptionType.TROLLEY_HANDLES]: () =>
        createAddableGrid(
          optionDb,
          AddableType.TrolleyHandle,
          Sides.BOTTOM,
          "AH34703",
        ),
      [VariableOptionType.ROLLER_BOARD]: () => createRollerBoard(optionDb),
      [VariableOptionType.RUNNERS]: () => createRunners(),
    };
  }

  public async update(changes: Partial<FlightCaseSettings>): Promise<void> {
    // Update settings with changes
    this.settings = { ...this.settings, ...changes };

    // Update blueprint using calculator's update method
    if (this.blueprint) {
      this.blueprint = this.blueprintCalculator.update(
        this.settings,
        this.blueprint,
        this.settings,
      );
    } else {
      // If no previous blueprint exists, do a full calculation
      this.blueprint = this.blueprintCalculator.execute(this.settings);
    }

    // Rebuild parts with updated settings/blueprint
    await this.build();
  }

  /**
   * Serialize the product definition
   */
  public serialize(): FlightCaseSettings {
    return { ...this.settings };
  }

  /**
   * Override build method to calculate group animations with rotation points after build steps
   */
  public async build(): Promise<void> {
    // Call parent build method first
    await super.build();

    // Initialize group animations after blueprint is ready
    if (this.blueprint) {
      // Find all hinge definitions from the parts
      const hingeDefinitions: Array<{
        group?: string;
        position: Vector3;
        partSelection?: { hingeOffset?: number };
        side?: Sides;
      }> = [];

      for (const part of Array.from(this.partDefinitions.values())) {
        const hingePart = part as {
          type?: string;
          group?: string;
          position?: Vector3;
          partSelection?: unknown;
          side?: Sides;
          edgeSide?: Sides;
          segmentLength?: number;
        };
        if (
          hingePart.type === "Hinge" &&
          "group" in part &&
          "position" in part &&
          hingePart.segmentLength == null &&
          hingePart.position !== undefined
        ) {
          hingeDefinitions.push({
            group: hingePart.group,
            position: hingePart.position,
            partSelection: hingePart.partSelection as
              | { hingeOffset?: number }
              | undefined,
            side: hingePart.side ?? hingePart.edgeSide,
          });
        }
      }

      // Use updateConnectors if manager has groups, otherwise initialize
      const hasExistingGroups =
        this.groupAnimationManager.getAvailableGroupNames().length > 0;

      if (hasExistingGroups) {
        // Update existing animations while preserving states
        this.groupAnimationManager.updateConnectors(
          this.blueprint.connectors,
          this.settings.cuts,
          hingeDefinitions,
          this.blueprint.innerDimensions,
        );
      } else {
        // First-time initialization
        this.groupAnimationManager.initialize(
          this.blueprint.connectors,
          this.settings.cuts,
          hingeDefinitions,
          this.blueprint.innerDimensions,
        );
      }

      // Calculate complete group animations with rotation points
      this.blueprintCalculator.calculateGroupAnimationsWithRotationPoints(
        this.blueprint,
        hingeDefinitions,
        this.settings,
      );
    }
  }

  /**
   * Animation Interface Methods - Diese werden vom ProductViewer verwendet
   */

  /**
   * Registriert eine Gruppe für Animationen
   */
  public registerAnimationGroup(groupName: string, groupRef: Group): boolean {
    return this.groupAnimationManager.registerGroup(groupName, groupRef);
  }

  /**
   * Schaltet den Zustand einer Gruppe um
   */
  public toggleAnimationGroup(groupName: string): boolean {
    return this.groupAnimationManager.toggleGroup(groupName);
  }

  /**
   * Prüft ob eine Gruppe interaktiv ist
   */
  public isGroupInteractive(groupName: string): boolean {
    return this.groupAnimationManager.isGroupInteractive(groupName);
  }

  /**
   * Prüft ob eine Gruppe registriert ist
   */
  public isGroupRegistered(groupName: string): boolean {
    return this.groupAnimationManager.isGroupRegistered(groupName);
  }

  /**
   * Gibt den Zustand einer Gruppe zurück
   */
  public getGroupAnimationState(
    groupName: string,
  ): GroupAnimationState | undefined {
    return this.groupAnimationManager.getGroupState(groupName);
  }

  /**
   * Gibt alle Gruppenzustände zurück
   */
  public getAllGroupAnimationStates(): Map<string, GroupAnimationState> {
    return this.groupAnimationManager.getAllGroupStates();
  }

  /**
   * Gibt alle verfügbaren Gruppennamen zurück
   */
  public getAvailableAnimationGroupNames(): string[] {
    return this.groupAnimationManager.getAvailableGroupNames();
  }

  /**
   * Cleanup für Animationen
   */
  public disposeAnimations(): void {
    this.groupAnimationManager.dispose();
  }

  /**
   * Generate DXF downloads for this flight case product
   * Groups parts by material/type and creates DXF files for each group
   */
  public override async getDownloads(
    context?: Record<string, unknown>,
  ): Promise<Download[]> {
    const priceData = context?.priceData as PriceData | undefined;
    // Filter for panel parts
    const panelParts: OuterPanelProperties[] = Array.from(
      this.partDefinitions.values(),
    ).filter(
      (part): part is OuterPanelProperties =>
        part.type === LTPartType.Panel || part.type === LTPartType.TrayPanel,
    );

    // Filter for pad parts
    const padParts: PadDefinition[] = Array.from(
      this.partDefinitions.values(),
    ).filter((part): part is PadDefinition => part.type === LTPartType.Pad);

    // Filter for profile parts
    const profileParts: ProfileProperties[] = Array.from(
      this.partDefinitions.values(),
    ).filter(
      (part): part is ProfileProperties => part.type === LTPartType.Profile,
    );

    // Filter for angle section parts
    const angleSectionParts: AngleSectionDefinition[] = Array.from(
      this.partDefinitions.values(),
    ).filter(
      (part): part is AngleSectionDefinition =>
        part.type === LTPartType.AngleSection,
    );

    const downloads: (Download | null)[] = [];

    // Generate panel downloads grouped by material
    if (panelParts.length > 0) {
      const panelsByMaterial = groupBy(
        panelParts,
        (part) => `${part.color?.name || "Unknown"} ${part.strength}mm`,
      );

      const panelDownloads = await Promise.all(
        Object.entries(panelsByMaterial).map(async ([materialDesc, panels]) => {
          try {
            const generator = new PanelDownloadGenerator(panels, materialDesc);
            return await generator.generateDownload();
          } catch (error) {
            console.error(
              `Failed to generate panel download for ${materialDesc}:`,
              error,
            );
            return null;
          }
        }),
      );
      downloads.push(...panelDownloads);
    }

    // Generate pad downloads grouped by material
    if (padParts.length > 0) {
      const padsByMaterial = groupBy(
        padParts,
        (part) => `${part.partSelection?.name || "Unknown"} ${part.strength}mm`,
      );

      const padDownloads = await Promise.all(
        Object.entries(padsByMaterial).map(async ([materialDesc, pads]) => {
          try {
            const generator = new PadDownloadGenerator(pads, materialDesc);
            return await generator.generateDownload();
          } catch (error) {
            console.error(
              `Failed to generate pad download for ${materialDesc}:`,
              error,
            );
            return null;
          }
        }),
      );
      downloads.push(...padDownloads);
    }

    // Generate profile download
    if (profileParts.length > 0) {
      try {
        const modelName = `${this.settings.type.toUpperCase()} ${this.blueprint?.innerDimensions.x}x${this.blueprint?.innerDimensions.y}x${this.blueprint?.innerDimensions.z}`;
        const profileDownload = await new ProfileDownloadGenerator(
          profileParts,
          angleSectionParts,
          modelName,
        ).generateDownload();
        downloads.push(profileDownload);
      } catch (error) {
        console.error("Failed to generate profile download:", error);
      }
    }

    // Generate parts list (Stückliste) CSV
    try {
      const partsListDownload = new PartsListDownload(
        this.partDefinitions,
        priceData,
      );
      downloads.push(partsListDownload);
    } catch (error) {
      console.error("Failed to generate parts list download:", error);
    }

    return downloads.filter((d): d is Download => d !== null);
  }
}
